<template>
  <div class="page-content">
    <breadcrumb-index :items="breadcrumbs">
      <template #actions>
        <select v-model="filterStatus" class="filter-select-modern" @change="fetchApprovals">
          <option value="">All Status</option>
          <option value="pending_review">Pending Review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="needs_rework">Needs Rework</option>
        </select>
        <select v-model="filterType" class="filter-select-modern">
          <option value="">All Types</option>
          <option value="question">Questions</option>
          <option value="notes">Notes</option>
          <option value="test">Tests</option>
          <option value="test_series">Test Series</option>
          <option value="exam">Exams</option>
          <option value="notification">Notifications</option>
        </select>
      </template>
    </breadcrumb-index>

    <DataTable
      :items="filteredApprovals"
      :columns="columns"
      :loading="loading"
      :searchable="false"
      empty-icon="check-circle"
      empty-title="No pending approvals"
      empty-description="All items have been reviewed"
    >
      <template #cell-type="{ item }">
        <span :class="['type-badge', getTypeBadge(item.contentType)]">{{ formatContentType(item.contentType) }}</span>
      </template>

      <template #cell-contentId="{ item }">
        <code class="content-id">{{ item.contentId.slice(0, 8) }}...</code>
      </template>

      <template #cell-version="{ item }">
        <span class="fw-medium">v{{ item.version }}</span>
      </template>

      <template #cell-submittedBy="{ item }">
        <span class="submitter-name">{{ getUserName(item.submittedBy) }}</span>
        <span class="text-muted d-block" style="font-size: 11px;">{{ formatDate(item.submittedAt) }}</span>
      </template>

      <template #cell-status="{ item }">
        <span :class="['status-badge', getStatusBadgeClass(item.status)]">
          {{ item.status.replace(/_/g, ' ') }}
        </span>
      </template>

      <template #cell-actions="{ item }">
        <div class="action-buttons">
          <button 
            class="action-btn action-view" 
            @click="viewItem(item)" 
            v-tooltip="'View details'"
          >
            <vue-feather type="eye" size="14" />
          </button>
          <button 
            v-if="canReview && item.status === 'pending_review'" 
            class="action-btn" 
            @click="approveItem(item)" 
            v-tooltip="{ text: 'Approve', variant: 'success' }"
            style="color: #10b981;"
          >
            <vue-feather type="check" size="14" />
          </button>
          <button 
            v-if="canReview && item.status === 'pending_review'" 
            class="action-btn" 
            @click="requestRework(item)" 
            v-tooltip="{ text: 'Request rework', variant: 'warning' }"
            style="color: #f59e0b;"
          >
            <vue-feather type="edit" size="14" />
          </button>
          <button 
            v-if="canReview && item.status === 'pending_review'" 
            class="action-btn action-delete" 
            @click="rejectItem(item)" 
            v-tooltip="{ text: 'Reject', variant: 'danger' }"
          >
            <vue-feather type="x" size="14" />
          </button>
        </div>
      </template>
    </DataTable>

    <!-- View Modal with Diff -->
    <div v-if="showViewModal" class="modal show d-block" tabindex="-1">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Approval Details</h5>
            <button type="button" class="btn-close" @click="showViewModal = false"></button>
          </div>
          <div class="modal-body" v-if="selectedItem">
            <!-- Metadata Row -->
            <div class="row mb-3">
              <div class="col-md-6">
                <p><strong>Content Type:</strong> <span :class="['type-badge', getTypeBadge(selectedItem.contentType)]">{{ formatContentType(selectedItem.contentType) }}</span></p>
                <p><strong>Content ID:</strong> <code>{{ selectedItem.contentId }}</code></p>
                <p><strong>Version:</strong> v{{ selectedItem.version }}</p>
              </div>
              <div class="col-md-6">
                <p><strong>Status:</strong> <span :class="['status-badge', getStatusBadgeClass(selectedItem.status)]">{{ selectedItem.status.replace(/_/g, ' ') }}</span></p>
                <p><strong>Submitted By:</strong> {{ getUserName(selectedItem.submittedBy) }} <span class="text-muted">· {{ formatDate(selectedItem.submittedAt) }}</span></p>
                <p v-if="selectedItem.reviewedBy"><strong>Reviewed By:</strong> {{ getUserName(selectedItem.reviewedBy) }} <span class="text-muted">· {{ formatDate(selectedItem.reviewedAt) }}</span></p>
              </div>
            </div>

            <div v-if="selectedItem.reviewNotes" class="mb-3">
              <strong>Review Notes:</strong>
              <p class="bg-light p-2 rounded">{{ selectedItem.reviewNotes }}</p>
            </div>

            <!-- Diff View Toggle -->
            <div class="diff-header mb-3">
              <div class="diff-tabs">
                <button 
                  :class="['diff-tab', { active: diffViewMode === 'side-by-side' }]" 
                  @click="diffViewMode = 'side-by-side'"
                >
                  <vue-feather type="columns" size="14" /> Side by Side
                </button>
                <button 
                  :class="['diff-tab', { active: diffViewMode === 'unified' }]" 
                  @click="diffViewMode = 'unified'"
                >
                  <vue-feather type="list" size="14" /> Unified
                </button>
                <button 
                  :class="['diff-tab', { active: diffViewMode === 'snapshot' }]" 
                  @click="diffViewMode = 'snapshot'"
                >
                  <vue-feather type="file-text" size="14" /> Snapshot Only
                </button>
              </div>
              <span v-if="loadingDiff" class="text-muted ms-2" style="font-size: 13px;">Loading previous version...</span>
            </div>

            <!-- Side-by-Side Diff -->
            <div v-if="diffViewMode === 'side-by-side' && diffFields.length" class="diff-container">
              <div class="diff-side-by-side">
                <div class="diff-panel diff-panel-old">
                  <div class="diff-panel-header">
                    <vue-feather type="clock" size="14" />
                    <span v-if="previousSnapshot">v{{ selectedItem.version - 1 }} — Previous</span>
                    <span v-else>No previous version</span>
                  </div>
                </div>
                <div class="diff-panel diff-panel-new">
                  <div class="diff-panel-header">
                    <vue-feather type="edit-3" size="14" />
                    <span>v{{ selectedItem.version }} — Proposed</span>
                  </div>
                </div>
              </div>
              <div v-for="field in diffFields" :key="field.key" class="diff-field-row">
                <div class="diff-field-label">{{ field.label }}</div>
                <div class="diff-side-by-side">
                  <div :class="['diff-cell', { 'diff-removed': field.changed }]">
                    <FormattedDiffValue :value="field.oldValue" :field-key="field.key" />
                  </div>
                  <div :class="['diff-cell', { 'diff-added': field.changed }]">
                    <FormattedDiffValue :value="field.newValue" :field-key="field.key" />
                  </div>
                </div>
              </div>
            </div>

            <!-- Unified Diff -->
            <div v-else-if="diffViewMode === 'unified' && diffFields.length" class="diff-container">
              <div class="diff-unified-header">
                <span v-if="previousSnapshot">Comparing v{{ selectedItem.version - 1 }} → v{{ selectedItem.version }}</span>
                <span v-else>New content (v{{ selectedItem.version }})</span>
              </div>
              <div v-for="field in diffFields" :key="field.key" class="diff-unified-row">
                <div class="diff-field-label">{{ field.label }}</div>
                <div v-if="!field.changed" class="diff-unified-cell diff-unchanged">
                  <FormattedDiffValue :value="field.newValue" :field-key="field.key" />
                </div>
                <template v-else>
                  <div v-if="field.oldValue !== undefined && field.oldValue !== null && field.oldValue !== ''" class="diff-unified-cell diff-removed">
                    <span class="diff-indicator">−</span>
                    <FormattedDiffValue :value="field.oldValue" :field-key="field.key" />
                  </div>
                  <div class="diff-unified-cell diff-added">
                    <span class="diff-indicator">+</span>
                    <FormattedDiffValue :value="field.newValue" :field-key="field.key" />
                  </div>
                </template>
              </div>
            </div>

            <!-- Snapshot Only (fallback / raw) -->
            <div v-else-if="diffViewMode === 'snapshot'" class="diff-container">
              <div v-if="selectedItem.contentSnapshot">
                <strong>Content Snapshot:</strong>
                <pre class="bg-light p-3 rounded mt-2" style="max-height: 400px; overflow: auto;">{{ formatSnapshot(selectedItem.contentSnapshot) }}</pre>
              </div>
              <div v-else class="text-muted">No content snapshot available.</div>
            </div>

            <div v-else-if="!diffFields.length && diffViewMode !== 'snapshot'" class="text-muted">
              No content snapshot available for comparison.
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="showViewModal = false">Close</button>
            <template v-if="canReview && selectedItem?.status === 'pending_review'">
              <button class="btn btn-warning" @click="requestRework(selectedItem)">Request Rework</button>
              <button class="btn btn-danger" @click="rejectItem(selectedItem)">Reject</button>
              <button class="btn btn-success" @click="approveItem(selectedItem)">Approve</button>
            </template>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showViewModal" class="modal-backdrop show"></div>
  </div>
</template>

<script>
import { ref, computed, onMounted, h, defineComponent } from 'vue'
import { useAuthStore } from '@/stores/auth'
import api from '@/services/api'
import { useNotifications } from '@/composables/useNotifications'
import DataTable from '@/components/data-table/DataTable.vue'

const API_BASE = '/v1/admin'

// Inline functional component for rendering diff values nicely
const FormattedDiffValue = defineComponent({
  name: 'FormattedDiffValue',
  props: {
    value: { default: null },
    fieldKey: { type: String, default: '' }
  },
  setup(props) {
    return () => {
      const val = props.value
      if (val === null || val === undefined || val === '') {
        return h('span', { class: 'text-muted fst-italic' }, '(empty)')
      }
      if (typeof val === 'boolean') {
        return h('span', { class: val ? 'text-success' : 'text-danger' }, val ? 'Yes' : 'No')
      }
      if (Array.isArray(val)) {
        if (val.length === 0) return h('span', { class: 'text-muted fst-italic' }, '(empty list)')
        return h('div', { class: 'diff-list' }, val.map((item, i) => {
          const text = typeof item === 'object' ? JSON.stringify(item, null, 2) : String(item)
          return h('div', { key: i, class: 'diff-list-item' }, text)
        }))
      }
      if (typeof val === 'object') {
        return h('pre', { class: 'diff-pre mb-0' }, JSON.stringify(val, null, 2))
      }
      return h('span', {}, String(val))
    }
  }
})

// Human-readable labels for common content fields
const FIELD_LABELS = {
  id: 'ID',
  title: 'Title',
  name: 'Name',
  description: 'Description',
  question_text: 'Question Text',
  questionText: 'Question Text',
  question_type: 'Question Type',
  questionType: 'Question Type',
  options: 'Options',
  correct_answer: 'Correct Answer',
  correctAnswer: 'Correct Answer',
  explanation: 'Explanation',
  difficulty: 'Difficulty',
  marks: 'Marks',
  negative_marks: 'Negative Marks',
  negativeMarks: 'Negative Marks',
  tags: 'Tags',
  duration: 'Duration',
  total_marks: 'Total Marks',
  totalMarks: 'Total Marks',
  total_questions: 'Total Questions',
  totalQuestions: 'Total Questions',
  instructions: 'Instructions',
  is_active: 'Active',
  isActive: 'Active',
  status: 'Status',
  subject: 'Subject',
  topic: 'Topic',
  exam: 'Exam',
  created_at: 'Created At',
  updated_at: 'Updated At'
}

function getFieldLabel(key) {
  return FIELD_LABELS[key] || key.replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function deepEqual(a, b) {
  if (a === b) return true
  if (a == null || b == null) return a == b
  if (typeof a !== typeof b) return false
  if (typeof a !== 'object') return false
  if (Array.isArray(a) !== Array.isArray(b)) return false
  const keysA = Object.keys(a)
  const keysB = Object.keys(b)
  if (keysA.length !== keysB.length) return false
  return keysA.every(k => deepEqual(a[k], b[k]))
}

function parseSnapshot(snapshot) {
  if (!snapshot) return null
  if (typeof snapshot === 'object') return snapshot
  try {
    return JSON.parse(snapshot)
  } catch {
    return null
  }
}

// Fields to skip in diff (internal/meta fields)
const SKIP_FIELDS = new Set(['id', 'created_at', 'createdAt', 'updated_at', 'updatedAt', 'version', 'contentType'])

export default {
  name: 'ApprovalsPage',
  components: { DataTable, FormattedDiffValue },
  setup() {
    const authStore = useAuthStore()
    const { toast, alert, promptTextarea } = useNotifications()
    const canReview = computed(() => authStore.isSuperAdmin || authStore.isAdmin || authStore.isReviewer)
    
    const breadcrumbs = [
      { title: 'Dashboard', url: '/app/dashboard' },
      { title: 'Approvals', active: true }
    ]

    const columns = [
      { key: 'type', label: 'Type', sortable: true },
      { key: 'contentId', label: 'Content ID' },
      { key: 'version', label: 'Version', sortable: true },
      { key: 'submittedBy', label: 'Submitted By', sortable: true },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'actions', label: 'Actions', class: 'text-end' }
    ]

    const approvals = ref([])
    const loading = ref(true)
    const filterType = ref('')
    const filterStatus = ref('pending_review')
    const showViewModal = ref(false)
    const selectedItem = ref(null)
    const previousSnapshot = ref(null)
    const loadingDiff = ref(false)
    const diffViewMode = ref('side-by-side')
    const userCache = ref({})

    const filteredApprovals = computed(() => {
      if (!filterType.value) return approvals.value
      return approvals.value.filter(a => a.contentType === filterType.value)
    })

    // Fetch and cache admin user names
    const fetchAdminUsers = async () => {
      try {
        const response = await api.get(`${API_BASE}/admin-users`)
        const users = Array.isArray(response.data) ? response.data : (response.data?.users || response.data?.data || [])
        users.forEach(u => {
          userCache.value[u.id] = u.fullName || u.email
        })
      } catch (err) {
        console.warn('Could not fetch admin users for name resolution:', err)
      }
    }

    const getUserName = (userId) => {
      if (!userId) return '-'
      return userCache.value[userId] || userId.slice(0, 8) + '...'
    }

    const formatContentType = (type) => {
      const labels = {
        question: 'Question',
        notes: 'Notes',
        test: 'Test',
        test_series: 'Test Series',
        exam: 'Exam',
        notification: 'Notification'
      }
      return labels[type] || type
    }

    const diffFields = computed(() => {
      if (!selectedItem.value) return []
      const newSnap = parseSnapshot(selectedItem.value.contentSnapshot)
      if (!newSnap) return []
      const oldSnap = parseSnapshot(previousSnapshot.value)

      const allKeys = new Set([
        ...Object.keys(newSnap),
        ...(oldSnap ? Object.keys(oldSnap) : [])
      ])

      const fields = []
      for (const key of allKeys) {
        if (SKIP_FIELDS.has(key)) continue
        const oldVal = oldSnap ? oldSnap[key] : undefined
        const newVal = newSnap[key]
        const changed = !deepEqual(oldVal, newVal)
        fields.push({
          key,
          label: getFieldLabel(key),
          oldValue: oldVal,
          newValue: newVal,
          changed
        })
      }

      // Sort: changed fields first, then alphabetical
      fields.sort((a, b) => {
        if (a.changed !== b.changed) return a.changed ? -1 : 1
        return a.label.localeCompare(b.label)
      })

      return fields
    })

    const fetchApprovals = async () => {
      loading.value = true
      try {
        const params = {}
        if (filterStatus.value) params.status = filterStatus.value
        const response = await api.get(`${API_BASE}/approvals`, { params })
        const data = response.data
        approvals.value = Array.isArray(data) ? data : (data?.approvals || data?.data || [])
      } catch (err) {
        console.error('Failed to fetch approvals:', err)
        toast.error('Failed to load approvals')
      } finally {
        loading.value = false
      }
    }

    const fetchPreviousVersion = async (item) => {
      if (item.version <= 1) {
        previousSnapshot.value = null
        return
      }
      loadingDiff.value = true
      try {
        const response = await api.get(`${API_BASE}/approvals`, {
          params: { contentType: item.contentType, contentId: item.contentId }
        })
        const versionData = response.data
        const versions = Array.isArray(versionData) ? versionData : (versionData?.approvals || versionData?.data || [])
        // Find the version just before the current one
        const prev = versions.find(v => v.version === item.version - 1)
        if (prev) {
          // If the version history entry has a snapshot, use it; otherwise fetch the full detail
          if (prev.contentSnapshot) {
            previousSnapshot.value = prev.contentSnapshot
          } else {
            const detail = await api.get(`${API_BASE}/approvals/${prev.id}`)
            previousSnapshot.value = detail.data?.contentSnapshot || null
          }
        } else {
          previousSnapshot.value = null
        }
      } catch (err) {
        console.warn('Could not fetch previous version:', err)
        previousSnapshot.value = null
      } finally {
        loadingDiff.value = false
      }
    }

    const getTypeBadge = (type) => {
      const badges = {
        question: 'type-question',
        notes: 'type-notes',
        test: 'type-test',
        test_series: 'type-test-series',
        exam: 'type-exam',
        notification: 'type-notification'
      }
      return badges[type] || 'type-default'
    }

    const getStatusBadgeClass = (status) => {
      const classes = {
        pending_review: 'status-pending',
        approved: 'status-active',
        rejected: 'status-error',
        needs_rework: 'status-pending'
      }
      return classes[status] || 'status-inactive'
    }

    const formatDate = (date) => {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    }

    const formatSnapshot = (snapshot) => {
      if (!snapshot) return ''
      if (typeof snapshot === 'string') {
        try { return JSON.stringify(JSON.parse(snapshot), null, 2) } catch { return snapshot }
      }
      return JSON.stringify(snapshot, null, 2)
    }

    const viewItem = async (item) => {
      try {
        const response = await api.get(`${API_BASE}/approvals/${item.id}`)
        selectedItem.value = response.data
        previousSnapshot.value = null
        diffViewMode.value = 'side-by-side'
        showViewModal.value = true
        // Fetch previous version in background
        fetchPreviousVersion(response.data)
      } catch (err) {
        toast.error('Failed to load approval details')
      }
    }

    const approveItem = async (item) => {
      const notes = await promptTextarea('Approve this item?', {
        placeholder: 'Add any notes (optional)...',
        confirmButtonText: 'Approve'
      })

      if (notes !== null) {
        try {
          await api.put(`${API_BASE}/approvals/${item.id}`, {
            id: item.id,
            action: 'approve',
            reviewedBy: authStore.user?.id,
            notes: notes || undefined
          })
          approvals.value = approvals.value.filter(a => a.id !== item.id)
          showViewModal.value = false
          toast.success('Item has been approved')
        } catch (err) {
          toast.error(err.response?.data?.error || 'Failed to approve item')
        }
      }
    }

    const rejectItem = async (item) => {
      const result = await alert.custom({
        title: 'Reject this item?',
        input: 'textarea',
        inputPlaceholder: 'Enter reason for rejection...',
        inputValidator: (value) => {
          if (!value) return 'Please provide a reason for rejection'
        },
        showCancelButton: true,
        confirmButtonText: 'Reject',
        customClass: {
          popup: 'swal-popup-custom',
          title: 'swal-title-custom',
          htmlContainer: 'swal-html-custom',
          confirmButton: 'swal-confirm-btn swal-btn-danger',
          cancelButton: 'swal-cancel-btn',
          input: 'swal-input-custom',
          actions: 'swal-actions-custom'
        }
      })

      if (result.isConfirmed && result.value) {
        try {
          await api.put(`${API_BASE}/approvals/${item.id}`, {
            id: item.id,
            action: 'reject',
            reviewedBy: authStore.user?.id,
            notes: result.value
          })
          approvals.value = approvals.value.filter(a => a.id !== item.id)
          showViewModal.value = false
          toast.info('Item has been rejected')
        } catch (err) {
          toast.error(err.response?.data?.error || 'Failed to reject item')
        }
      }
    }

    const requestRework = async (item) => {
      const result = await alert.custom({
        title: 'Request changes?',
        input: 'textarea',
        inputPlaceholder: 'Describe the required changes...',
        inputValidator: (value) => {
          if (!value) return 'Please describe the required changes'
        },
        showCancelButton: true,
        confirmButtonText: 'Request Rework'
      })

      if (result.isConfirmed && result.value) {
        try {
          await api.put(`${API_BASE}/approvals/${item.id}`, {
            id: item.id,
            action: 'rework',
            reviewedBy: authStore.user?.id,
            notes: result.value
          })
          approvals.value = approvals.value.filter(a => a.id !== item.id)
          showViewModal.value = false
          toast.info('Rework request has been sent')
        } catch (err) {
          toast.error(err.response?.data?.error || 'Failed to request rework')
        }
      }
    }

    onMounted(() => {
      fetchAdminUsers()
      fetchApprovals()
    })

    return {
      breadcrumbs,
      columns,
      approvals,
      loading,
      filterType,
      filterStatus,
      filteredApprovals,
      showViewModal,
      selectedItem,
      previousSnapshot,
      loadingDiff,
      diffViewMode,
      diffFields,
      canReview,
      getTypeBadge,
      getStatusBadgeClass,
      formatDate,
      formatSnapshot,
      formatContentType,
      getUserName,
      viewItem,
      approveItem,
      rejectItem,
      requestRework,
      fetchApprovals
    }
  }
}
</script>

<style scoped>
.content-id {
  font-size: 12px;
  padding: 4px 8px;
  background: #f1f5f9;
  border-radius: 4px;
}

:global(body.dark-mode) .content-id {
  background: #334155;
}

/* Type Badges */
.type-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
}

.type-question {
  background: #fef3c7;
  color: #92400e;
}

.type-notes {
  background: #ede9fe;
  color: #5b21b6;
}

.type-test {
  background: #e0f2fe;
  color: #075985;
}

.type-test-series {
  background: #dbeafe;
  color: #1e40af;
}

.type-exam {
  background: #dcfce7;
  color: #166534;
}

.type-notification {
  background: #fce7f3;
  color: #9d174d;
}

.type-default {
  background: #f1f5f9;
  color: #475569;
}

:global(body.dark-mode) .type-question {
  background: rgba(245, 158, 11, 0.15);
  color: #fbbf24;
}

:global(body.dark-mode) .type-notes {
  background: rgba(139, 92, 246, 0.15);
  color: #a78bfa;
}

:global(body.dark-mode) .type-test {
  background: rgba(14, 165, 233, 0.15);
  color: #38bdf8;
}

:global(body.dark-mode) .type-test-series {
  background: rgba(59, 130, 246, 0.15);
  color: #60a5fa;
}

:global(body.dark-mode) .type-exam {
  background: rgba(34, 197, 94, 0.15);
  color: #4ade80;
}

:global(body.dark-mode) .type-notification {
  background: rgba(236, 72, 153, 0.15);
  color: #f472b6;
}

:global(body.dark-mode) .type-default {
  background: rgba(100, 116, 139, 0.15);
  color: #94a3b8;
}

/* Submitter name */
.submitter-name {
  font-weight: 500;
  font-size: 13px;
}

/* Diff Header & Tabs */
.diff-header {
  display: flex;
  align-items: center;
}

.diff-tabs {
  display: flex;
  gap: 4px;
  background: #f1f5f9;
  border-radius: 8px;
  padding: 3px;
}

:global(body.dark-mode) .diff-tabs {
  background: #1e293b;
}

.diff-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: none;
  background: transparent;
  border-radius: 6px;
  font-size: 13px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s;
}

.diff-tab:hover {
  color: #334155;
  background: rgba(255, 255, 255, 0.5);
}

.diff-tab.active {
  background: #fff;
  color: #1e293b;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

:global(body.dark-mode) .diff-tab {
  color: #94a3b8;
}

:global(body.dark-mode) .diff-tab:hover {
  color: #e2e8f0;
  background: rgba(255, 255, 255, 0.05);
}

:global(body.dark-mode) .diff-tab.active {
  background: #334155;
  color: #f1f5f9;
}

/* Diff Container */
.diff-container {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

:global(body.dark-mode) .diff-container {
  border-color: #334155;
}

/* Side-by-Side */
.diff-side-by-side {
  display: grid;
  grid-template-columns: 1fr 1fr;
}

.diff-panel-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.diff-panel-old .diff-panel-header {
  background: #fef2f2;
  color: #991b1b;
  border-right: 1px solid #e2e8f0;
}

.diff-panel-new .diff-panel-header {
  background: #f0fdf4;
  color: #166534;
}

:global(body.dark-mode) .diff-panel-old .diff-panel-header {
  background: rgba(239, 68, 68, 0.1);
  color: #fca5a5;
  border-right-color: #334155;
}

:global(body.dark-mode) .diff-panel-new .diff-panel-header {
  background: rgba(34, 197, 94, 0.1);
  color: #86efac;
}

.diff-field-row {
  border-top: 1px solid #e2e8f0;
}

:global(body.dark-mode) .diff-field-row {
  border-top-color: #334155;
}

.diff-field-label {
  padding: 6px 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
  background: #f8fafc;
}

:global(body.dark-mode) .diff-field-label {
  background: #0f172a;
  color: #94a3b8;
}

.diff-cell {
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.5;
  word-break: break-word;
  border-right: 1px solid #e2e8f0;
  min-height: 40px;
}

.diff-cell:last-child {
  border-right: none;
}

:global(body.dark-mode) .diff-cell {
  border-right-color: #334155;
}

.diff-removed {
  background: #fef2f2;
}

.diff-added {
  background: #f0fdf4;
}

:global(body.dark-mode) .diff-removed {
  background: rgba(239, 68, 68, 0.08);
}

:global(body.dark-mode) .diff-added {
  background: rgba(34, 197, 94, 0.08);
}

/* Unified Diff */
.diff-unified-header {
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  background: #f8fafc;
  color: #64748b;
  border-bottom: 1px solid #e2e8f0;
}

:global(body.dark-mode) .diff-unified-header {
  background: #0f172a;
  color: #94a3b8;
  border-bottom-color: #334155;
}

.diff-unified-row {
  border-top: 1px solid #e2e8f0;
}

:global(body.dark-mode) .diff-unified-row {
  border-top-color: #334155;
}

.diff-unified-cell {
  padding: 8px 12px;
  font-size: 13px;
  line-height: 1.5;
  display: flex;
  gap: 8px;
  word-break: break-word;
}

.diff-unified-cell.diff-unchanged {
  padding-left: 32px;
}

.diff-unified-cell.diff-removed {
  background: #fef2f2;
  color: #991b1b;
}

.diff-unified-cell.diff-added {
  background: #f0fdf4;
  color: #166534;
}

:global(body.dark-mode) .diff-unified-cell.diff-removed {
  background: rgba(239, 68, 68, 0.08);
  color: #fca5a5;
}

:global(body.dark-mode) .diff-unified-cell.diff-added {
  background: rgba(34, 197, 94, 0.08);
  color: #86efac;
}

.diff-indicator {
  font-weight: 700;
  font-family: monospace;
  flex-shrink: 0;
  width: 14px;
  text-align: center;
}

/* Diff value helpers */
.diff-list-item {
  padding: 2px 0;
  font-size: 13px;
}

.diff-list-item + .diff-list-item {
  border-top: 1px dashed #e2e8f0;
}

:global(body.dark-mode) .diff-list-item + .diff-list-item {
  border-top-color: #334155;
}

.diff-pre {
  font-size: 12px;
  background: transparent;
  white-space: pre-wrap;
}
</style>
